import loggerFactory from '../logger';
import { x } from '../utils';

const log = loggerFactory.get('socket-enabler:pubsub');

function join(first, second) {
    const result = `${first}/${second}`;
    return result.replace(/\/+/, '/');
}

function toMulti(destination) {
    return `${destination}/multi`;
}

function resolveItemIdsHeader(itemIds, provideVersionTag) {
    const itemIdsStr = itemIds.map(id => {
        let versionTag;
        provideVersionTag(tag => versionTag = tag, id);
        return typeof versionTag !== 'undefined' ? `${id},${versionTag}` : `${id}`
    }).join(';');
    return `${itemIdsStr};`
}

function resolveSubscriptionKey(itemIds) {
    return itemIds?.sort().join('-');
}

function isMulti(subscription) {
    return subscription && subscription.isMulti;
}

function cleanKey(key, part) {
    const variants = [`-${part}`, `${part}-`, part];
    return variants.reduce((result, part) => {
        return result.replace(part, '');
    }, key)
}

function removeFromMulti(destination, itemId, subscriptions) {
    const multiDestination = toMulti(destination);
    Object.keys(subscriptions)
        .filter(key => key.includes(multiDestination))
        .forEach(key => {
            const { itemIds } = subscriptions[key];
            const indexToRemove = itemIds.indexOf(itemId);
            if (indexToRemove > -1) {
                const updatedKey = cleanKey(key, itemId);
                const subscription = subscriptions[key];
                subscription.itemIds.splice(indexToRemove, 1);
                delete subscriptions[key];
                if (subscription.itemIds.length > 0) {
                    subscriptions[updatedKey] = subscription;
                }
            }
        })
}

function Pubsub(client, responseBus) {
    const subscriptions = {};
    const multiSubscriptions = {};

    // Send all subscriptions on network drop/reconnect. Here we will iterate through existing single subscriptions and restore multi subscriptions if
    // subscription.parent isMulti. Later on we will subscribe to them one by one.
    client.onReady(() => {
        const subscriptionsQueue = Object.keys(subscriptions).reduce((result, singleDestination) => {
            const subscription = subscriptions[singleDestination];
            if (isMulti(subscription.parent)) {
                const multiSubscription = subscription.parent;
                if (!result.includes(multiSubscription)) {
                    result.push(multiSubscription);
                }
            } else {
                result.push(subscription);
            }
            return result;
        }, []);

        subscriptionsQueue.forEach(subscription => {
            const { destination, isMulti } = subscription;
            if (isMulti) {
                const { itemIds, provideVersionTag, headerLocale, name } = subscription;
                const itemIdsStr = resolveItemIdsHeader(itemIds, provideVersionTag);
                const key = resolveSubscriptionKey(itemIds);
                subscription.provideVersionTag(() => {
                    client.subscribeMultiple(destination, destination, headerLocale, itemIdsStr, key, name)
                }, subscription.id);

            } else {
                const { dataId, name } = subscription;
                subscription.provideVersionTag(versionTag => {
                    client.subscribe(destination, destination, versionTag, subscription.headerLocale, dataId, name);
                }, subscription.id);
            }
        })
    });

    // Route updates
    client.onReceive(function (message) {
        const destination = message.headers.destination;
        const subscription = subscriptions[destination];

        if (subscription) {
            if (message.body && message.body.length === 0) {
                log.warn('Received an empty response from server.');
                return;
            }
            try {
                const handler = subscription.handler;
                const type = message.headers.type;
                const data = JSON.parse(message.body);
                handler(type, data, subscription.id);
            } catch (e) {
                log.error('Failed to handle incoming message', e);
            }
        }
    });

    this.subscribe = (destination, headerLocale, provideVersionTag, onSubscribe, onUpdate, dataId, name) => {
        const dispose = x.once(() => {
            client.unsubscribe(destination);
            delete subscriptions[destination];
            responseBus.removeHandler(destination);
        });

        if (provideVersionTag === undefined) {
            throw new Error('No provideVersionTag provided');
        }
        if (!x.isFunction(provideVersionTag)) {
            throw new Error('provideVersionTag must be a function');
        }
        if (!onSubscribe) {
            throw new Error('No onSubscribe provided');
        }
        if (!onUpdate) {
            throw new Error('No onUpdate provided');
        }
        if (subscriptions[destination]) {
            throw new Error(`Subscription for "${destination}" already registered`);
        }

        // SETUP handler for initial response...
        responseBus.addHandler(
            destination,
            (err, data, headers) => {
                if (err) {
                    dispose();
                }
                onSubscribe(err, headers && headers.type, data);
            },
            true
        );

        // SETUP handler for subsequent updates...
        subscriptions[destination] = {
            handler: onUpdate,
            provideVersionTag: provideVersionTag,
            headerLocale: headerLocale,
            destination,
            dataId, name
        };

        // id === destination by design, so we can route initial response correctly (through responseBus)
        provideVersionTag(versionTag =>
            client.isReady() && client.subscribe(destination, destination, versionTag, headerLocale, dataId, name)
        );

        return {
            dispose: dispose,
        };
    };

    this.subscribeMultiple = (destination, ids, headerLocale, provideVersionTag, onSubscribe, onUpdate, name) => {
        const createDispose = id => {
            return x.once(() => {
                const itemDestination = join(destination, id);
                client.unsubscribe(itemDestination);
                delete subscriptions[itemDestination];
                removeFromMulti(destination, id, multiSubscriptions);
                responseBus.removeHandler(itemDestination);
            })
        }
        if (provideVersionTag === undefined) {
            throw new Error('No provideVersionTag provided');
        }
        if (!x.isFunction(provideVersionTag)) {
            throw new Error('provideVersionTag must be a function');
        }
        if (!onSubscribe) {
            throw new Error('No onSubscribe provided');
        }
        if (!onUpdate) {
            throw new Error('No onUpdate provided');
        }

        const multiDestination = toMulti(destination);
        const key = resolveSubscriptionKey(ids);

        // SETUP handler for initial response...
        responseBus.addMultiHandler(
            multiDestination,
            (err, data, headers) => {
                if (err) {
                    ids.map(createDispose).forEach(dispose => dispose());
                }
                if (!data) {
                    onSubscribe(err);
                    return;
                }
                const dataInfos = Object.keys(data).map(id => {
                    const dataPart = data[id];
                    return {id, type: dataPart.t, data: dataPart.s}
                })
                onSubscribe(err, headers.type, dataInfos);
                responseBus.removeMultiHandler(multiDestination, key);
            },
            true,
            key
        );

        const multiKey = `${multiDestination}/${key}`;
        multiSubscriptions[multiKey] = {
            destination: multiDestination,
            headerLocale,
            itemIds: ids,
            provideVersionTag,
            isMulti: true,
            name
        };

        ids.forEach(itemId => {
            const itemDestination = join(destination, itemId);
            // SETUP handler for subsequent updates...
            subscriptions[itemDestination] = {
                id: itemId,
                handler: onUpdate,
                provideVersionTag: provideVersionTag,
                headerLocale: headerLocale,
                parent: multiSubscriptions[multiKey],
            };
        })

        if (client.isReady()) {
            const itemIdsStr = resolveItemIdsHeader(ids, provideVersionTag);
            client.subscribeMultiple(multiDestination, multiDestination, headerLocale, itemIdsStr, key, name);
        }

        return ids.map(id => ({id, dispose: createDispose(id)}))
    };
}

export default Pubsub;
